// =============================================================
// Department store — persisted to localStorage, reactive via
// useSyncExternalStore so every component re-renders on change.
// =============================================================
import { useSyncExternalStore } from "react";

const STORAGE_KEY_AGENT = "impact:departments:agent";
const STORAGE_KEY_IT = "impact:departments:it";

// ── Default lists (matches original hardcoded values) ──────────
const DEFAULT_AGENT: string[] = [
  "ACCOUNTING",
  "DRUGZONE",
  "LEGACY HEALTH US",
  "BONITA",
  "MGP",
  "CORE RX RETURNS",
  "EMPIRE RX",
  "SUPPORT MANAGEMENT",
];

const DEFAULT_IT: string[] = [
  "Software",
  "Hardware",
  "System",
  "Network",
  "Access",
];

// ── Read / write helpers ──────────────────────────────────────
function read(key: string, fallback: string[]): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: string[]) {
  localStorage.setItem(key, JSON.stringify(value));
  // Notify listeners
  listeners.forEach((l) => l());
}

// ── Subscription (useSyncExternalStore) ───────────────────────
type Listener = () => void;
let listeners: Set<Listener> = new Set();

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Snapshot references — only recreated when we write.
let agentSnap: string[] | null = null;
let itSnap: string[] | null = null;

function getAgentSnapshot(): string[] {
  if (!agentSnap) agentSnap = read(STORAGE_KEY_AGENT, DEFAULT_AGENT);
  return agentSnap;
}

function getItSnapshot(): string[] {
  if (!itSnap) itSnap = read(STORAGE_KEY_IT, DEFAULT_IT);
  return itSnap;
}

// Invalidate snapshots when we write
const origWrite = write;
function writeAndInvalidate(key: string, value: string[]) {
  agentSnap = null;
  itSnap = null;
  origWrite(key, value);
}

// ── Public hooks ──────────────────────────────────────────────
export function useAgentDepartments(): string[] {
  return useSyncExternalStore(subscribe, getAgentSnapshot, getAgentSnapshot);
}

export function useItDepartments(): string[] {
  return useSyncExternalStore(subscribe, getItSnapshot, getItSnapshot);
}

/** All departments (agent + IT) */
export function useAllDepartments(): string[] {
  const agent = useAgentDepartments();
  const it = useItDepartments();
  return [...agent, ...it];
}

// ── Mutation helpers ──────────────────────────────────────────
export type DeptGroup = "agent" | "it";

function keyFor(group: DeptGroup) {
  return group === "agent" ? STORAGE_KEY_AGENT : STORAGE_KEY_IT;
}
function defaultFor(group: DeptGroup) {
  return group === "agent" ? DEFAULT_AGENT : DEFAULT_IT;
}

export function addDepartment(group: DeptGroup, name: string) {
  const current = read(keyFor(group), defaultFor(group));
  if (current.some((d) => d.toLowerCase() === name.toLowerCase())) return false;
  writeAndInvalidate(keyFor(group), [...current, name]);
  return true;
}

export function removeDepartment(group: DeptGroup, name: string) {
  const current = read(keyFor(group), defaultFor(group));
  writeAndInvalidate(
    keyFor(group),
    current.filter((d) => d !== name),
  );
}

export function renameDepartment(
  group: DeptGroup,
  oldName: string,
  newName: string,
) {
  const current = read(keyFor(group), defaultFor(group));
  if (
    current.some(
      (d) => d.toLowerCase() === newName.toLowerCase() && d !== oldName,
    )
  )
    return false;
  writeAndInvalidate(
    keyFor(group),
    current.map((d) => (d === oldName ? newName : d)),
  );
  return true;
}

/** Move a department up or down in the list */
export function reorderDepartment(
  group: DeptGroup,
  name: string,
  direction: "up" | "down",
) {
  const current = read(keyFor(group), defaultFor(group));
  const idx = current.indexOf(name);
  if (idx === -1) return;
  const swapIdx = direction === "up" ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= current.length) return;
  const next = [...current];
  const tmp = next[idx]!;
  next[idx] = next[swapIdx]!;
  next[swapIdx] = tmp;
  writeAndInvalidate(keyFor(group), next);
}

/** Reset a group back to defaults */
export function resetDepartments(group: DeptGroup) {
  writeAndInvalidate(keyFor(group), defaultFor(group));
}
